import merge from 'lodash/merge'
import settings from '@/settings'

import RestClient from './base_clients/rest_client'
import { camelCaseTransformer, errorHandlingInterceptor, paramsSerializer } from './helpers'
import store from '@/store'
import { powInterceptor } from './security'
import { ACCOUNT_QUERY_KEY } from '@/constants'

const baseURL = settings.get('API_URL')

const authInterceptor = (config) => {
  config.headers['X-Auth-Token'] = store.state.auth.token

  return config
}

/**
 * Cobrand Portal API Client
 *
 * @see https://rtd.lesta.group/docs/co-brand-portal-api/en/latest/api/http/cobrand_portal.html
 * @todo fix 404, docs aren't there
 */
export class CobrandAPIClient extends RestClient {
  constructor (options) {
    super(options)

    this.client.defaults.paramsSerializer = paramsSerializer
    this.client.defaults.transformResponse.push(camelCaseTransformer)
    this.client.interceptors.response.use(null, errorHandlingInterceptor)
    this.client.interceptors.request.use(authInterceptor)
    this.client.interceptors.request.use(powInterceptor)
  }

  /**
   * Get cards list
   *
   * @param {number | string} acc_id
   * @param {Object} options
   * @param {string[]} options.include
   * @param {{ limit: number, offset: number }} options.page
   */
  async fetchCards (accountId, options = {}) {
    const defaultParams = { filter: { [ACCOUNT_QUERY_KEY]: accountId } }
    const params = merge({}, defaultParams, options)
    const response = await this.client.get('/cards', { params })
    const { meta, data: cards } = response.data
    return { cards, meta }
  }

  /**
   * Get cards bonuses
   *
   * @param {number | string} acc_id
   * @param {{ game: string, accrual_type: string, accrual_group: string }} options
   */
  async fetchCardsBonuses (accountId, options = {}) {
    const defaultParams = { filter: { [ACCOUNT_QUERY_KEY]: accountId } }
    const params = merge({}, defaultParams, options)
    const response = await this.client.get('/bonuses', { params })
    const { data, meta } = response.data

    return { bonuses: data, meta }
  }

  /**
   * Change game card is bound to
   *
   * @param {Object} bindingData
   * @param {number | string} bindingData.acc_id
   * @param {string} bindingData.game
   * @param {string} bindingData.card_code
   */
  async changeCardGame (bindingData) {
    const response = await this.client.post('/cards/change_game', bindingData, { proofOfWork: true })

    return response.data
  }

  /**
   * Fetch card designs
   */
  async getCardDesigns () {
    const response = await this.client.get('/cards/designs')
    return response.data
  }

  /**
   * Change card design
   *
   * @param {Object} bindingData
   * @param {number | string} bindingData.acc_id
   * @param {string} bindingData.design
   * @param {string} bindingData.card_code
   */
  async changeCardDesign (bindingData) {
    const response = await this.client.post('/cards/change_design', bindingData, { proofOfWork: true })

    return response.data
  }

  /**
   * Bind card to account
   *
   * @param {Object} bindingData
   * @param {number | string} bindingData.acc_id
   * @param {string} bindingData.game
   * @param {string} bindingData.card_code
   */
  async bindCard (bindingData) {
    const response = await this.client.post('/cards/bind', bindingData, { proofOfWork: true })

    return response.data
  }

  /**
   * Get card info by card code
   *
   * @param {Object} bindingData
   * @param {number | string} bindingData.acc_id
   * @param {string} bindingData.card_code
   */
  async getInfoCard (bindingData) {
    const response = await this.client.get('/cards/info', { params: bindingData, proofOfWork: true })

    return response.data
  }

  /**
   * Fetch accruals by `cardId`
   *
   * @param {number | string} acc_id
   * @param {number} cardId
   * @param {string[]} options.include
   * @param {Object} options
   * @param {{ limit: number, offset: number }} options.page
   */
  async fetchAccruals (accountId, cardId, options = {}) {
    const defaultParams = {
      filter: { [ACCOUNT_QUERY_KEY]: accountId, card_id: cardId },
      page: { limit: 20 }
    }
    const params = merge({}, defaultParams, options)
    const response = await this.client.get('/accruals', { params })
    const { data: accruals, meta } = response.data

    return { accruals, meta }
  }

  /**
   * Fetch Actions (Offers)
   * @param {Object} options
   * @param {string} options.game
   * @param {{ limit: number, offset: number }} options.page
   */
  async fetchOffers (options = {}) {
    const defaultParams = {
      page: { limit: 30 },
      sort: '-start_date'
    }
    const params = merge({}, defaultParams, options)
    const response = await this.client.get('/offers', { params })
    const { data: offers, meta } = response.data

    return { offers, meta }
  }

  async fetchRandomRewardsTasks (accountId) {
    const response = await this.client.get('/random/tasks', { params: { filter: { [ACCOUNT_QUERY_KEY]: accountId } } })
    const { data } = response
    return { data }
  }

  async fetchRandomRewards () {
    const response = await this.client.get('/random/rewards')
    const { data } = response
    return { data }
  }

  // eslint-disable-next-line camelcase
  async participateRandomRewards (accountId, card_code) {
    const response = await this.client.post('/random/participate', { [ACCOUNT_QUERY_KEY]: accountId, card_code }, { proofOfWork: true })
    const { data } = response
    return { data }
  }

  async playRandomRewards (accountId) {
    const response = await this.client.post('/random/play', { [ACCOUNT_QUERY_KEY]: accountId }, { proofOfWork: true })
    const { data } = response
    return { data }
  }
  async socialRandomRewards (accountId) {
    const response = await this.client.post('/random/social', { [ACCOUNT_QUERY_KEY]: accountId }, { proofOfWork: true })
    const { data } = response
    return { data }
  }
  async getRandomButton () {
    const response = await this.client.get('/random/button')
    const { data } = response
    return { data }
  }
  async getPageContent () {
    const response = await this.client.get('/ui_content')
    const { data } = response
    return { data }
  }
  async fetchQuizRandomRewards (accountId) {
    const response = await this.client.get('/random/quiz', { params: { filter: { [ACCOUNT_QUERY_KEY]: accountId } } })
    const { data } = response
    return { data }
  }
  async postQuizRandomRewards (accountId) {
    const response = await this.client.post('/random/quiz', { [ACCOUNT_QUERY_KEY]: accountId }, { proofOfWork: true })
    const { data } = response
    return { data }
  }

  async fetchExtraRewardsConditions (partner) {
    const response = await this.client.get('/conditions', { params: { filter: { partner } } })
    const { data } = response
    return { data }
  }

  async fetchExtraRewardsPrograms (partner) {
    const response = await this.client.get('/programs', { params: { filter: { partner } } })
    const { data } = response
    return { data }
  }

  async extraBonusCheck (payload) {
    const response = await this.client.post('/extra_bonus/check', payload, { proofOfWork: true })
    const { data } = response
    return { data }
  }
}

export default new CobrandAPIClient({
  baseURL: `${baseURL}/v1`
})

export const cobrandV2APIClient = new CobrandAPIClient({
  baseURL: `${baseURL}/v2`
})
